import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check, MessageCircle, Tag, FolderCheck, Languages, HeartHandshake } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { MaskedLine, Reveal, Eyebrow } from "../components/Reveal";
import Marquee from "../components/Marquee";
import Seo from "../components/Seo";
import ServiceIcon, { SERVICE_ORDER } from "../components/ServiceIcon";
import Logo from "../components/Logo";

const HERO_IMG =
  "https://images.unsplash.com/photo-1708748513828-2227f6d39c04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjA1NzR8MHwxfHNlYXJjaHw0fHxzYW5kJTIwZHVuZXMlMjBzdW5zZXQlMjBnb2xkZW4lMjBsaWdodCUyMHdhcm18ZW58MHx8fHwxNzkxODM0ODIxfDA&ixlib=rb-4.1.0&q=85";
const FAMILY_IMG =
  "https://images.unsplash.com/photo-1683650666842-9b50b48caabf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxoaXNwYW5pYyUyMGZhbWlseSUyMHNtaWxpbmclMjB3YXJtJTIwaG9tZSUyMGRvY3VtZW50JTIwcHJlcGFyYXRpb258ZW58MHx8fHwxNzkxODMzNDk4fDA&ixlib=rb-4.1.0&q=85";

const WHY_ICONS = [MessageCircle, Tag, FolderCheck, Languages, HeartHandshake];

export default function HomePage() {
  const { t } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const sunY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <main data-testid="home-page">
      <Seo title={t.seo.home.title} desc={t.seo.home.desc} />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden bg-cream grain pt-28 md:pt-36 pb-16 md:pb-24">
        <motion.div
          style={{ y: sunY }}
          className="pointer-events-none absolute right-[8%] top-24 hidden h-28 w-28 rounded-full bg-terracotta/80 blur-[2px] lg:block"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal y={16}>
                <Eyebrow>{t.hero.eyebrow}</Eyebrow>
              </Reveal>
              <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] text-espresso" data-testid="hero-headline">
                {t.hero.lines.map((line, i) => (
                  <MaskedLine key={line} delay={0.15 + i * 0.18}>
                    {i === 2 ? <em className="not-italic text-terracotta">{line}</em> : line}
                  </MaskedLine>
                ))}
              </h1>
              <Reveal delay={0.7}>
                <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-espresso/75" data-testid="hero-subtext">
                  {t.hero.sub}
                </p>
                <div className="mt-9 flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/services"
                    data-testid="hero-explore-services-button"
                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-espresso px-8 py-4 text-base font-semibold text-cream transition-colors duration-300 hover:bg-terracotta active:scale-95"
                  >
                    {t.common.exploreServices}
                    <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  <Link
                    to="/contact"
                    data-testid="hero-get-started-button"
                    className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-espresso px-8 py-4 text-base font-semibold text-espresso transition-colors duration-300 hover:bg-espresso hover:text-cream active:scale-95"
                  >
                    {t.common.getStarted}
                  </Link>
                </div>
                <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-2" data-testid="hero-trust-points">
                  {t.hero.trust.map((point) => (
                    <li key={point} className="flex items-center gap-2 text-sm font-medium text-espresso/70">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-teal/15">
                        <Check className="h-3 w-3 text-teal" strokeWidth={3} />
                      </span>
                      {point}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>

            <div className="relative lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 40, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="relative"
              >
                <div className="arch-frame relative overflow-hidden shadow-[0_30px_60px_-15px_rgba(56,39,25,0.35)]" data-testid="hero-image-frame">
                  <motion.img
                    style={{ y: imgY, scale: 1.15, objectPosition: "center center" }}
                    src={HERO_IMG}
                    alt="Southwestern desert dunes meeting the coast at sunset"
                    className="h-[420px] sm:h-[520px] w-full object-cover"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-espresso/25 via-transparent to-transparent" />
                </div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.9 }}
                  className="absolute -bottom-8 -left-4 sm:-left-10 rounded-2xl border border-sand/50 bg-cream/90 p-4 shadow-xl backdrop-blur-md"
                  data-testid="hero-logo-card"
                >
                  <Logo withText size={56} />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* SERVICES */}
      <section className="bg-cream py-20 md:py-28" data-testid="home-services-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>{t.servicesIndex.eyebrow}</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-medium leading-snug text-espresso">
              {t.home.helpTitle}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-espresso/75">{t.home.helpSub}</p>
          </Reveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICE_ORDER.map((slug, i) => {
              const s = t.services[slug];
              const isOther = slug === "other";
              return (
                <Reveal key={slug} delay={0.05 * i} className={isOther ? "sm:col-span-2 lg:col-span-1" : ""}>
                  <Link
                    to={`/services/${slug}`}
                    data-testid={`service-card-${slug}`}
                    className={`group flex h-full flex-col rounded-2xl border p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(56,39,25,0.25)] ${
                      isOther
                        ? "border-espresso bg-espresso text-cream"
                        : "border-sand/50 bg-white text-espresso hover:border-terracotta/50"
                    }`}
                  >
                    <span
                      className={`flex h-12 w-12 items-center justify-center rounded-full ${
                        isOther ? "bg-sand/20 text-sand" : "bg-teal/10 text-teal"
                      }`}
                    >
                      <ServiceIcon slug={slug} />
                    </span>
                    <h3 className="mt-5 font-serif text-xl sm:text-2xl font-medium">{s.name}</h3>
                    <p className={`mt-3 flex-1 text-sm leading-relaxed ${isOther ? "text-cream/70" : "text-espresso/70"}`}>
                      {s.short}
                    </p>
                    <div className="mt-6 flex items-center justify-between">
                      {s.price ? (
                        <span
                          className={`font-mono text-sm font-medium ${
                            isOther ? "text-sand" : "text-terracotta"
                          }`}
                          data-testid={`service-card-price-${slug}`}
                        >
                          {s.price}
                          {s.priceNote ? ` ${s.priceNote}` : ""}
                        </span>
                      ) : (
                        <span className="font-mono text-sm text-sand">{t.common.customQuote}</span>
                      )}
                      <span
                        className={`inline-flex items-center gap-1.5 text-sm font-semibold ${
                          isOther ? "text-sand" : "text-teal"
                        }`}
                      >
                        {t.common.learnMore}
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="relative overflow-hidden bg-white py-20 md:py-28" data-testid="why-choose-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <Eyebrow>{t.nav.about}</Eyebrow>
                <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-medium leading-snug text-espresso">
                  {t.home.whyTitle}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-espresso/75">{t.home.whySub}</p>
              </Reveal>
              <Reveal delay={0.15} className="mt-10">
                <div className="arch-frame overflow-hidden shadow-lg">
                  <img
                    src={FAMILY_IMG}
                    alt="A family together at home reviewing documents"
                    className="h-72 w-full object-cover"
                    loading="lazy"
                  />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ol className="space-y-2">
                {t.home.whyPoints.map((point, i) => {
                  const Icon = WHY_ICONS[i];
                  return (
                    <Reveal key={point.title} delay={0.06 * i}>
                      <li className="group flex gap-5 rounded-2xl border border-transparent p-5 transition-colors duration-300 hover:border-sand/60 hover:bg-cream" data-testid={`why-point-${i}`}>
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal/10 text-teal transition-colors duration-300 group-hover:bg-teal group-hover:text-white">
                          <Icon className="h-5 w-5" strokeWidth={1.75} />
                        </span>
                        <div>
                          <h3 className="font-serif text-lg font-medium text-espresso">{point.title}</h3>
                          <p className="mt-1 text-sm leading-relaxed text-espresso/70">{point.text}</p>
                        </div>
                      </li>
                    </Reveal>
                  );
                })}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* 3-STEP PROCESS */}
      <section className="bg-sand/25 py-20 md:py-28" data-testid="home-steps-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>{t.nav.howItWorks}</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-medium leading-snug text-espresso">
              {t.home.stepsTitle}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-espresso/75">{t.home.stepsSub}</p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {t.home.steps.map((step, i) => (
              <Reveal key={step.title} delay={0.1 * i}>
                <div className="relative h-full rounded-2xl border border-sand/50 bg-white p-8 pt-10" data-testid={`home-step-${i + 1}`}>
                  <span className="absolute -top-6 left-8 font-serif text-6xl font-semibold text-terracotta/90">
                    {i + 1}
                  </span>
                  <h3 className="mt-6 font-serif text-xl font-medium text-espresso">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-espresso/70">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10">
            <Link
              to="/how-it-works"
              data-testid="home-steps-learn-more"
              className="group inline-flex items-center gap-2 text-base font-semibold text-teal hover:text-espresso transition-colors"
            >
              {t.common.learnMore}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-espresso py-20 md:py-28 grain" data-testid="home-final-cta">
        <div className="pointer-events-none absolute -left-20 top-10 h-64 w-64 rounded-full bg-terracotta/20 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -right-16 bottom-0 h-72 w-72 rounded-full bg-teal/20 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-cream">
              {t.home.ctaTitle}
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-base sm:text-lg leading-relaxed text-cream/75">
              {t.home.ctaText}
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                to="/contact"
                data-testid="home-cta-get-started-button"
                className="rounded-full bg-terracotta px-8 py-4 text-base font-semibold text-white transition-colors duration-300 hover:bg-sand hover:text-espresso active:scale-95"
              >
                {t.common.getStarted}
              </Link>
              <Link
                to="/services"
                data-testid="home-cta-services-button"
                className="rounded-full border-2 border-cream/40 px-8 py-4 text-base font-semibold text-cream transition-colors duration-300 hover:border-cream hover:bg-cream hover:text-espresso active:scale-95"
              >
                {t.common.exploreServices}
              </Link>
            </div>
            <p className="mt-8 text-xs text-cream/50">{t.home.ctaNote}</p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
